import type { ErrorInfo, ReactNode } from 'react'
import React, { Suspense } from 'react'
import { GlobalError } from '@/shared/ui/global-error'

type ErrorBoundaryProps = {
  children: ReactNode
  fallback?: (error: Error, errorInfo: ErrorInfo, resetError: () => void) => ReactNode
}

type ErrorBoundaryState = {
  hasError: boolean
  error: Error | null
  errorInfo: ErrorInfo | null
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    }
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return {
      hasError: true,
      error,
    }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo)

    this.setState({
      error,
      errorInfo,
    })

    // Здесь можно добавить отправку ошибки в сервис мониторинга
    // например, Sentry, LogRocket и т.д.
  }

  resetError = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    })
  }

  render() {
    const { hasError, error, errorInfo } = this.state
    const { children, fallback } = this.props

    if (hasError && error) {
      // Если передан кастомный fallback, используем его
      if (fallback) {
        return fallback(error, errorInfo!, this.resetError)
      }

      // Иначе используем стандартный GlobalError
      return (
        <Suspense fallback={<div>Загрузка...</div>}>
          <GlobalError
            error={error}
            errorInfo={{
              componentStack: errorInfo?.componentStack || undefined,
            }}
            resetError={this.resetError}
          />
        </Suspense>
      )
    }

    return children
  }
}
